import api from '../config/api';
import { Raffle, User, Withdrawal, PaginatedResponse } from '../types';

export interface CountryConfig {
  id: string;
  countryCode: string;
  configKey: string;
  configValue: string;
  country?: { name: string; currency: string };
  createdAt: string;
  updatedAt: string;
}

export interface GlobalConfigItem {
  id: string;
  key: string;
  value: string;
  createdAt: string;
  updatedAt: string;
}

export interface AdminWithdrawal extends Withdrawal {
  wallet?: {
    user: { firstName: string; lastName: string; email: string };
  };
}

export interface FinanceSummary {
  revenue: {
    platformFees: number;
    platformFeeCount: number;
    withdrawalFees: number;
    withdrawalFeeCount: number;
    grossRevenue: number;
    affiliateCommissions: number;
    affiliateCommissionCount: number;
    netRevenue: number;
  };
  operations: {
    totalDraws: number;
    totalDrawSales: number;
    totalOrganizerPayouts: number;
    totalTicketsSold: number;
    totalTicketSales: number;
  };
}

export interface LedgerEntry {
  id: string;
  type: 'PLATFORM_FEE' | 'WITHDRAWAL_FEE' | 'AFFILIATE_COMMISSION';
  amount: number;
  currency: string;
  description: string;
  referenceId?: string;
  referenceType?: string;
  metadata?: string;
  createdAt: string;
}

export const adminService = {
  // Get all raffles
  getAllRaffles: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<Raffle>> => {
    const response = await api.get('/admin/raffles', { params });
    return response.data;
  },

  // Get all users
  getAllUsers: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<User>> => {
    const response = await api.get('/admin/users', { params });
    return response.data;
  },

  // Get user by ID
  getUser: async (id: string): Promise<{ success: boolean; data: User }> => {
    const response = await api.get(`/admin/users/${id}`);
    return response.data;
  },

  // Update user
  updateUser: async (id: string, data: { firstName?: string; lastName?: string; phone?: string; role?: string; isActive?: boolean }): Promise<{ success: boolean; data: User; message: string }> => {
    const response = await api.put(`/admin/users/${id}`, data);
    return response.data;
  },

  // Update config
  updateConfig: async (key: string, value: string): Promise<{ success: boolean; data: any }> => {
    const response = await api.post('/admin/config', { key, value });
    return response.data;
  },

  // Get config
  getConfig: async (): Promise<{ success: boolean; data: GlobalConfigItem[] }> => {
    const response = await api.get('/admin/config');
    return response.data;
  },

  // Approve withdrawal
  approveWithdrawal: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/admin/withdrawals/${id}/approve`);
    return response.data;
  },

  // Finalize withdrawal (send to Paystack for processing)
  finalizeWithdrawal: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/admin/withdrawals/${id}/finalize`);
    return response.data;
  },

  // Get withdrawals
  getWithdrawals: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<{ success: boolean; data: { withdrawals: AdminWithdrawal[]; pagination: any } }> => {
    const response = await api.get('/admin/withdrawals', { params });
    return response.data;
  },

  // Trigger draw
  triggerDraw: async (raffleId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/admin/draws/${raffleId}/trigger`);
    return response.data;
  },

  // Country config
  getCountryConfigs: async (countryCode?: string): Promise<{ success: boolean; data: CountryConfig[] }> => {
    const response = await api.get('/admin/country-config', { params: countryCode ? { countryCode } : {} });
    return response.data;
  },

  upsertCountryConfig: async (countryCode: string, configKey: string, configValue: string): Promise<{ success: boolean; data: CountryConfig }> => {
    const response = await api.post('/admin/country-config', { countryCode, configKey, configValue });
    return response.data;
  },

  deleteCountryConfig: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/admin/country-config/${id}`);
    return response.data;
  },

  // Finance
  getFinanceSummary: async (params?: { startDate?: string; endDate?: string }): Promise<{ success: boolean; data: FinanceSummary }> => {
    const response = await api.get('/admin/finance/summary', { params });
    return response.data;
  },

  getFinanceLedger: async (params?: { type?: string; page?: number; limit?: number; startDate?: string; endDate?: string }): Promise<{ success: boolean; data: { entries: LedgerEntry[]; pagination: any } }> => {
    const response = await api.get('/admin/finance/ledger', { params });
    return response.data;
  },
};
